"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Clock, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Helper function to format date
const formatDate = (date) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(date).toLocaleDateString("en-IN", options);
};

export default function CustomerOrder() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Get token & userId from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");
  const navigate = useNavigate();

  // ✅ Redirect if not logged in
  useEffect(() => {
    if (!authToken || !authUserId) {
      console.error("Auth token or userId not found in localStorage");
      navigate("/login");
    }
  }, [authToken, authUserId, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("User not logged in. Please login first.");
          setOrders([]);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          "https://code.gurusport.in/api/get-user-orders",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("response", response.data);

        if (response?.data && Array.isArray(response?.data?.data)) {
          setOrders(response.data.data);
        } else {
          setError("No orders found.");
          setOrders([]);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to fetch orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-semibold">
            <Clock size={16} /> Pending
          </span>
        );
      case "shipped":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
            <Truck size={16} /> Shipped
          </span>
        );
      case "reached":
      case "delivered":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
            <CheckCircle size={16} /> Delivered
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-200 text-gray-700 text-sm font-semibold">
            {status || "Unknown"}
          </span>
        );
    }
  };

  return (
    <div className="p-6 md:p-10 bg-gray-50 dark:bg-gray-950 min-h-screen">
      {/* Heading */}
      <h1 className="text-3xl font-bold mb-8 text-center">📦 My Orders</h1>

      {/* Loading State */}
      {loading && (
        <p className="text-center text-gray-600 dark:text-gray-300">
          Loading your orders...
        </p>
      )}

      {/* Error State */}
      {error && !loading && (
        <p className="text-center text-red-500 font-medium">{error}</p>
      )}

      {/* Orders List */}
      {!loading && !error && (
        <div className="space-y-6">
          {orders.length > 0 ? (
            orders.map((order) => (
              <div
                key={order.id}
                className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg hover:shadow-xl transition-all p-5"
              >
                {/* Order Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b dark:border-gray-700">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                      Order #{order.order_code}
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
                      <p>
                        <span className="font-medium">Order Date:</span>{" "}
                        {formatDate(order.order_date)}
                      </p>
                      <p>
                        <span className="font-medium">Payment Status:</span>{" "}
                        {order.payment_status}
                      </p>
                      <p style={{ marginTop: "-6px" }}>
                        <span className="font-medium">Total Amount:</span>{" "}
                        <span className="text-lg font-bold text-red-600 dark:text-red-400">
                          ₹{Number(order.total_amount).toLocaleString("en-IN")}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 md:mt-0">
                    {getStatusBadge(order.order_status)}
                  </div>
                </div>

                <div className="space-y-4">
                  {order.order_details.data.map((orderDetail) => (
                    <div
                      key={orderDetail.id}
                      className="flex flex-col md:flex-row gap-4 items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
                    >
                      {/* Product Image */}
                      <img
                        src={
                          orderDetail.products.default_image ||
                          "/placeholder.svg"
                        }
                        alt={orderDetail.products.name}
                        className="w-24 h-24 object-contain rounded-lg border dark:border-gray-600"
                      />

                      {/* Product Details */}
                      <div className="flex-1 w-full">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                          {orderDetail.products.name}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                          {orderDetail.products.short_description}
                        </p>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
                          <p className="flex items-center gap-2">
                            <span className="font-medium">Color:</span>
                            <div className="flex items-center gap-1">
                              {orderDetail.colors.hexa_code_2 ? (
                                // Split color circle for dual colors
                                <div className="relative w-4 h-4 rounded-full border border-gray-300 overflow-hidden">
                                  <div
                                    className="absolute inset-0 w-1/2 h-full"
                                    style={{
                                      backgroundColor:
                                        orderDetail.colors.hexa_code,
                                    }}
                                  ></div>
                                  <div
                                    className="absolute inset-0 w-1/2 h-full right-0"
                                    style={{
                                      backgroundColor:
                                        orderDetail.colors.hexa_code_2,
                                    }}
                                  ></div>
                                </div>
                              ) : (
                                // Single color circle
                                <span
                                  className="inline-block w-4 h-4 rounded-full border border-gray-300"
                                  style={{
                                    backgroundColor:
                                      orderDetail.colors.hexa_code,
                                  }}
                                ></span>
                              )}
                              <span>{orderDetail.colors.name}</span>
                            </div>
                          </p>
                          <p>
                            <span className="font-medium">Size:</span>{" "}
                            {orderDetail.sizes.name}
                          </p>
                          <p>
                            <span className="font-medium">Quantity:</span>{" "}
                            {orderDetail.quantity}
                          </p>
                          <p>
                            <span className="font-medium">Price:</span>{" "}
                            <span className="font-bold text-red-600 dark:text-red-400">
                              ₹
                              {Number(orderDetail.price).toLocaleString(
                                "en-IN"
                              )}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* Delivery Status */}
                      <div className="flex-shrink-0">
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                          {order.order_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-600 dark:text-gray-300">
              You have no orders yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
